import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import { flowRight as compose } from 'lodash';
import withStyles from 'isomorphic-style-loader/withStyles';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import cx from 'classnames';

import { openWishListModal } from '../../actions/WishList/modalActions';
import { openLoginModal } from '../../actions/modalActions';
import { getwishListStatus } from '../../helpers/wishListStatus';
import messages from '../../locale/messages';
import { withQuery } from '../../hoc/withGraphql';

import heartImage from '/public/SiteIcons/heartIconOff.svg';
import heartIconFill from '/public/SiteIcons/heartIconOn.svg'
import heartImageView from '/public/SiteIcons/heartIconOff.svg';
import heartIconFillView from '/public/SiteIcons/heartIconOn.svg'


//Styles
import s from './WishListIcon.css';
import cs from '../commonStyle.css';

const WishListIcon = ({
    isChecked = false,
    type = 'icon',
    listId,
    isAuthenticated,
    wishListModalStatus,
    wishListRoomId,
    openLoginModal,
    openWishListModal,
    isViewListing,
    heartIcon,
    key
}) => {
    const [iconSelected, setIconSelected] = useState(isChecked);
    const [clicked, setClicked] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
    const prevModalStatus = useRef(wishListModalStatus);
    const longPressTimer = useRef(null);
    const triggerRef = useRef(null);

    useEffect(() => {
        if (!clicked && !wishListModalStatus) {
            setIconSelected(isChecked);
        }
    }, [isChecked, clicked, wishListModalStatus]);

    useEffect(() => {
        const updateWishListStatus = async () => {
            if (prevModalStatus.current === true && wishListModalStatus === false) {
                const currentWishListStatus = await getwishListStatus(listId);
                setIconSelected(currentWishListStatus);
            }
            prevModalStatus.current = wishListModalStatus;
        };

        updateWishListStatus();
    }, [wishListModalStatus, listId]);

    const updateTooltipPosition = useCallback(() => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setTooltipPos({
                top: rect.top - 8,
                left: rect.left + rect.width / 2
            });
        }
    }, []);

    const handleIconAction = () => {
        if (isAuthenticated) {
            setClicked(true);
            openWishListModal(listId, isViewListing);
        } else {
            openLoginModal();
        }
    };

    const handleMouseEnter = () => {
        updateTooltipPosition();
        setShowTooltip(true);
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
    };

    const handleTouchStart = () => {
        updateTooltipPosition();
        longPressTimer.current = setTimeout(() => {
            setShowTooltip(true);
        }, 500);
    };

    const handleTouchEnd = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
        }
        setTimeout(() => {
            setShowTooltip(false);
        }, 2000);
    };

    const tooltipMessage = iconSelected ? messages.wishListTooltipRemove : messages.wishListTooltipAdd;

    const tooltipContent = showTooltip && (
        <span
            className={cx(s.tooltip, s.tooltipFixed)}
            style={{ top: tooltipPos.top, left: tooltipPos.left }}
        >
            <FormattedMessage {...tooltipMessage} />
        </span>
    );

    const renderIcon = () => (
        <div
            ref={triggerRef}
            className={cx(s.iconContainer, heartIcon, 'heartIconRtl')}
            onClick={handleIconAction}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            <img
                src={iconSelected ? heartIconFill : heartImage}
                className={cx(s.wishListIcon)}
                alt={'Wishlist'}
            />
            {createPortal(tooltipContent, document.body)}
        </div>
    );

    const renderButton = () => (
        <div
            ref={triggerRef}
            className={cx(s.buttonContainer, s.iconAlignCenter)}
            onClick={handleIconAction}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            <span className={s.iconWrapper}>
                <img
                    src={iconSelected ? heartIconFillView : heartImageView}
                    className={cx(s.wishListIcon, s.vtrMiddle, s.heardIconSize)}
                    alt={'Wishlist'}
                />
            </span>
            <span className={cx(s.buttonText, s.vtrBottom, cs.flexShrinkNone)}>
                {iconSelected 
                    ? <FormattedMessage {...messages.saved} /> 
                    : <FormattedMessage {...messages.wishListSave} />
                }
            </span>
            {createPortal(tooltipContent, document.body)}
        </div>
    );

    return (
        <div key={key}>
            {type === 'icon' ? renderIcon() : renderButton()}
        </div>
    );
};

WishListIcon.propTypes = {
    isChecked: PropTypes.bool,
    type: PropTypes.string,
    listId: PropTypes.number.isRequired,
    isAuthenticated: PropTypes.bool,
    wishListModalStatus: PropTypes.bool,
    wishListRoomId: PropTypes.number,
    openLoginModal: PropTypes.func.isRequired,
    openWishListModal: PropTypes.func.isRequired,
    isViewListing: PropTypes.bool,
    heartIcon: PropTypes.string,
    key: PropTypes.string
};

const mapState = (state) => ({
    isAuthenticated: state.runtime.isAuthenticated,
    wishListModalStatus: state.modalStatus.wishListModalOpen,
    wishListRoomId: state.modalStatus.listId
});

const mapDispatch = {
    openLoginModal,
    openWishListModal
};

// GraphQL query
const WISHLIST_QUERY = gql`
    query UserListing($listId: Int!, $preview: Boolean) {
        UserListing (listId: $listId, preview: $preview) {
            wishListStatus
        }
    }     
`;

export default compose(
    injectIntl,
    withStyles(s, cs),
    connect(mapState, mapDispatch),
    withQuery(WISHLIST_QUERY, {
        options: (props) => ({
            variables: {
                listId: props?.listId
            },
            ssr: false
        })
    })
)(WishListIcon);

